import { useState } from 'react';
import { api } from '../../shared/api/client';
import { Button, Card, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';

interface Stats {
  members: number;
  newMembers: number;
  conversions: number;
  waterBaptisms: number;
  spiritBaptisms: number;
  attendance: number | null;
  sundaySchool: number | null;
  firstVisits: number;
}
interface Area {
  id: string;
  name: string;
  kind: string;
  title: string;
  churches: { id: string; name: string; slug: string; section: string; shared: boolean; stats: Stats | null }[];
}

const TITLES: Record<string, string> = { presbitero: 'Presbítero', superintendente: 'Superintendente', secretario: 'Secretario' };
const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const pad = (n: number) => String(n).padStart(2, '0');
/** Solo en esta página: la tabla completa sale en hoja horizontal, sin barra de desplazamiento. */
const PRINT_CSS = '@media print { @page { size: landscape; } .ad-overseer .table-wrap { overflow: visible !important; } .ad-overseer .table { width: 100%; font-size: 9pt; } }';
const COLS: [keyof Stats, string][] = [
  ['members', 'Miembros'],
  ['newMembers', 'Nuevos'],
  ['conversions', 'Convertidos'],
  ['waterBaptisms', 'Bautizados en agua'],
  ['spiritBaptisms', 'Bautizados en el Espíritu'],
  ['attendance', 'Asistencia promedio'],
  ['sundaySchool', 'Escuela Dominical'],
  ['firstVisits', 'Visitantes'],
];

/** Asambleas de Dios · el presbítero ve el resumen de las iglesias de su sección o distrito que lo comparten. */
export function AdOverseerPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState<number | null>(now.getMonth() + 1);
  const from = month ? `${year}-${pad(month)}-01` : `${year}-01-01`;
  const to = month ? `${year}-${pad(month)}-${pad(new Date(year, month, 0).getDate())}` : `${year}-12-31`;
  const data = useAsync((signal) => api.get<{ areas: Area[] }>(`/multichurch/ad/overseer?from=${from}&to=${to}`, signal), [from, to]);

  return (
    <div className="page ad-report ad-overseer">
      <style>{PRINT_CSS}</style>
      <header className="page__header">
        <p className="eyebrow">Asambleas de Dios</p>
        <h1>Mi sección y distrito</h1>
        <p className="muted">
          Las iglesias a tu cargo. Solo ves los números de las que decidieron compartir su informe con su sección o distrito; nunca nombres de personas.
        </p>
      </header>
      <div className="actions actions--wrap no-print" role="group" aria-label="Periodo">
        <div className="field">
          <label htmlFor="ov-month">Mes</label>
          <select id="ov-month" value={month ?? 0} onChange={(e) => setMonth(Number(e.target.value) || null)}>
            <option value={0}>Todo el año</option>
            {MONTHS.map((m, i) => (
              <option key={m} value={i + 1}>
                {m}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="ov-year">Año</label>
          <select id="ov-year" value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {Array.from({ length: 6 }, (_, i) => now.getFullYear() - i).map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <Button variant="secondary" onClick={() => window.print()}>
          Imprimir o guardar PDF
        </Button>
      </div>
      <p className="small muted">Periodo: {month ? `${MONTHS[month - 1]} ${year}` : `Año ${year}`}</p>
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {data.status === 'ready'
        ? data.data.areas.map((a) => {
            const shared = a.churches.filter((c) => c.stats);
            const total = (k: keyof Stats) => shared.reduce((n, c) => n + (c.stats![k] ?? 0), 0);
            // Asistencia y Escuela Dominical son promedios: en la fila de total se promedian (solo entre las iglesias que tienen dato).
            const average = (k: 'attendance' | 'sundaySchool') => {
              const values = shared.map((c) => c.stats![k]).filter((v): v is number => v !== null && v !== undefined);
              return values.length ? Math.round(values.reduce((n, v) => n + v, 0) / values.length) : '—';
            };
            return (
              <Card key={a.id} title={`${a.name} · ${TITLES[a.title] ?? a.title}`}>
                {a.churches.length === 0 ? <EmptyState title="Todavía no hay iglesias en esta red." /> : null}
                {a.churches.length ? (
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Iglesia</th>
                          {COLS.map(([, l]) => (
                            <th key={l}>{l}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {a.churches.map((c) => (
                          <tr key={c.id}>
                            <td>
                              <a href={`/i/${c.slug}`} target="_blank" rel="noopener noreferrer">
                                {c.name}
                              </a>
                              <span className="small muted"> · {c.section}</span>
                            </td>
                            {c.stats ? (
                              COLS.map(([k]) => <td key={k}>{c.stats![k] ?? '—'}</td>)
                            ) : (
                              <td colSpan={COLS.length} className="small muted">
                                No comparte su informe
                              </td>
                            )}
                          </tr>
                        ))}
                        {shared.length > 1 ? (
                          <tr>
                            <td>
                              <strong>Total ({shared.length} iglesias)</strong>
                            </td>
                            {COLS.map(([k]) => (
                              <td key={k} title={k === 'attendance' || k === 'sundaySchool' ? 'Promedio de las iglesias' : undefined}>
                                <strong>{k === 'attendance' || k === 'sundaySchool' ? average(k) : total(k)}</strong>
                              </td>
                            ))}
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                ) : null}
              </Card>
            );
          })
        : null}
    </div>
  );
}
